import { useSyncExternalStore } from "react";

/**
 * How the sale summary is ordered, grouped and what it shows, shared by the
 * settlements list and its toolbar - same module-level store as
 * useSelectedInvoices, since App.tsx renders the two from separate <Routes>
 * trees. The contract page's sale summary reads the same store, through its
 * own inline toggles.
 */
export type SaleSummaryViewOptions = {
  contractNumberSort: "asc" | "desc";
  // Settlements list only - a single contract's summary is never grouped.
  groupByClient: boolean;
  counterReadings: boolean;
  profitability: boolean;
  inflation: boolean;
};

// Profitability and inflation are remembered per browser. Storage can be
// unavailable (private mode, blocked site data), in which case they just
// start shown on every visit. The rest start at their defaults on every visit.
const STORAGE_KEYS: Partial<Record<keyof SaleSummaryViewOptions, string>> = {
  profitability: "saleSummary.showProfitability",
  inflation: "saleSummary.showInflation",
};

const readStored = (key: keyof SaleSummaryViewOptions): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEYS[key] ?? "") !== "false";
  } catch {
    return true;
  }
};

let viewOptions: SaleSummaryViewOptions = {
  contractNumberSort: "desc",
  groupByClient: false,
  // Off by default: the counter strip is an answer to "where did this reading
  // come from?", not something every month needs on screen.
  counterReadings: false,
  profitability: readStored("profitability"),
  inflation: readStored("inflation"),
};
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => viewOptions;

export const setSaleSummaryViewOption = <K extends keyof SaleSummaryViewOptions>(
  key: K,
  value: SaleSummaryViewOptions[K]
) => {
  viewOptions = { ...viewOptions, [key]: value };
  const storageKey = STORAGE_KEYS[key];
  if (storageKey) {
    try {
      localStorage.setItem(storageKey, String(value));
    } catch {
      // Not remembered - the toggle still works for this visit.
    }
  }
  listeners.forEach((listener) => listener());
};

export const useSaleSummaryViewOptions = () =>
  useSyncExternalStore(subscribe, getSnapshot);
